<template>
  <div class="flex flex-col gap-4">
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <PaymentFormInput
        v-model="firstname"
        label="First name"
        :error-message="firstErrorMessage(v$.firstname)"
        data-cy="firstname"
      />
      <PaymentFormInput
        v-model="lastname"
        label="Last name"
        :error-message="firstErrorMessage(v$.lastname)"
        data-cy="lastname"
      />
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <PaymentFormSelect
        v-model="accountType"
        label="Account type"
        placeholder="Select account type"
        :options="accountTypes"
        :error-message="firstErrorMessage(v$.accountType)"
        data-cy="account-type"
      />
      <PaymentFormSelect
        v-model="holderType"
        label="Account usage"
        placeholder="Select business or personal"
        :options="holderTypes"
        :error-message="firstErrorMessage(v$.holderType)"
        data-cy="account-usage"
      />
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <PaymentFormInput
        v-model="routingNumber"
        label="Routing number"
        inputmode="numeric"
        :error-message="firstErrorMessage(v$.routingNumber)"
        data-cy="routing-number"
      />
      <PaymentFormInput
        v-model="accountNumber"
        label="Account number"
        inputmode="numeric"
        :error-message="firstErrorMessage(v$.accountNumber)"
        data-cy="account-number"
      />
    </div>

    <div id="submit" class="hidden"></div>

    <SaveForFuturePayments
      v-if="canSaveCard"
      v-model="savePaymentMethod"
      data-cy="stax-ach-save-payment-method"
    />
  </div>
</template>

<script lang="ts">
import { handleStaxError } from "@/lib/helpers.js";
import { firstErrorMessage } from "@/lib/vuelidate_helpers";

import PaymentFormInput from "@/components/PaymentModals/PaymentFormInput.vue";
import PaymentFormSelect from "@/components/PaymentModals/PaymentFormSelect.vue";
import SaveForFuturePayments from "@/components/PaymentModals/SaveForFuturePayments.vue";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import { loadStax } from "./paymentSdks";
import usePaymentMethods from "./usePaymentMethods";

declare const StaxJs: any;

export default {
  components: {
    PaymentFormInput,
    PaymentFormSelect,
    SaveForFuturePayments
  },
  props: {
    autoSaveCard: {
      type: Boolean,
      default: false
    },
    customer: {
      type: Object,
      default: null
    }
  },
  setup() {
    const { paymentMethodsResponse } = usePaymentMethods();
    return {
      paymentMethodsResponse,
      v$: useVuelidate(),
      firstErrorMessage
    };
  },
  validations() {
    return {
      firstname: { required, $autoDirty: true },
      lastname: { required, $autoDirty: true },
      accountType: { required, $autoDirty: true },
      holderType: { required, $autoDirty: true },
      routingNumber: { required, $autoDirty: true },
      accountNumber: { required, $autoDirty: true }
    };
  },
  data() {
    return {
      accountTypes: [
        { label: "Checking", value: "checking" },
        { label: "Savings", value: "savings" }
      ],
      holderTypes: [
        { label: "Personal", value: "personal" },
        { label: "Business", value: "business" }
      ],
      staxJs: null as any,
      firstname: "",
      lastname: "",
      primaryAddress: {} as any,
      routingNumber: "",
      accountNumber: "",
      accountType: "checking",
      holderType: "",
      savePaymentMethod: false
    };
  },
  computed: {
    config(): any {
      return this.paymentMethodsResponse.find(
        (method: any) => method.key === "stax_ach"
      );
    },
    canSaveCard(): boolean {
      return !!this.config?.card_savable && !this.autoSaveCard;
    }
  },
  async mounted() {
    try {
      await loadStax();
      if (this.sdkLoadAborted) return;
      this.initForm();
    } catch (error) {
      window.notify.error(
        "Unable to load the payment form. Please refresh and try again."
      );
      window.Sentry?.captureException(
        error instanceof Error ? error : new Error(String(error))
      );
    }
  },
  beforeUnmount() {
    // The SDK load is async; if the form unmounts first, skip initForm so the
    // SDK can't bind its hosted-field iframe onto another form's shared DOM ids.
    this.sdkLoadAborted = true;
  },
  methods: {
    async getData() {
      (window as any).notify.close();

      const validated = await this.v$.$validate();
      if (!validated) {
        throw new Error();
      }

      const details = {
        method: "bank",
        firstname: this.firstname,
        lastname: this.lastname,
        person_name: `${this.firstname} ${this.lastname}`,
        address_1: this.primaryAddress.street,
        address_city: this.primaryAddress.city,
        address_state: this.primaryAddress.state,
        address_zip: this.primaryAddress.zip,
        bank_type: this.accountType,
        bank_holder_type: this.holderType,
        bank_account: this.accountNumber,
        bank_routing: this.routingNumber
      };
      try {
        const response = await this.staxJs.tokenize(details);
        return {
          transaction_type: "ach",
          ach_first_name: this.firstname,
          ach_last_name: this.lastname,
          ach_token: response.id,
          ach_account_type: this.accountType,
          ach_account_number: this.accountNumber,
          ach_routing_number: this.routingNumber,
          ach_save_payment_method: this.savePaymentMethod || this.autoSaveCard
        };
      } catch (err) {
        handleStaxError(this, err);
      }
    },
    initForm() {
      if (!this.config) {
        return;
      }

      if (this.customer) {
        this.firstname = this.customer.first_name;
        this.lastname = this.customer.last_name;
        this.primaryAddress =
          this.customer.primary_address ||
          this.customer.addresses.find((address: any) => address.primary);
      }

      this.staxJs = new StaxJs(this.config.web_id, {});
    }
  }
};
</script>
